import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  Button,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  OutlinedInput,
  Box,
  Chip,
  Tabs,
  Tab,
  RadioGroup,
  Radio,
  FormLabel,
} from "@mui/material";
import { doc, collection, addDoc, setDoc, updateDoc } from "firebase/firestore";
import { db } from "../../@shared/firebase/config";

const availableRoles = [
  "player",
  "ip_provider",
  "collection_creator",
  "admin",
  "test",
];

export default function EditUserModal({
  open,
  onClose,
  users,
  selectedUserIds,
  onConfirm,
}) {
  const isBulk = selectedUserIds.length > 1;
  const selectedUser = !isBulk
    ? users.find((u) => u.id === selectedUserIds[0])
    : null;
  const [tabIndex, setTabIndex] = useState(0);

  const [formData, setFormData] = useState({
    email: "",
    username: "",
    name: "",
    xp: 0,
    level: 1,
    emailVerified: false,
    infinityTokens: 0,
    roles: [],
  });
  const [customProfileFields, setCustomProfileFields] = useState([
    { key: "", type: "string", value: "" },
  ]);

  const collections = [
    "Character cards",
    "Infinity cards",
    "decks",
    "questLog",
    "quests",
    "rewards",
    "friends",
    "notifications",
    "avatars",
    "cosmetics",
    "selectors",
  ];

  const [selectedCollection, setSelectedCollection] = useState("");
  const [customCollection, setCustomCollection] = useState("");
  const [subDocFields, setSubDocFields] = useState([
    { key: "", type: "string", value: "" },
  ]);
  const [docMode, setDocMode] = useState("auto"); // 'auto' | 'custom' | 'edit'
  const [docId, setDocId] = useState("");

  useEffect(() => {
    if (!isBulk && selectedUser) {
      setFormData({
        email: selectedUser.email || "",
        username: selectedUser.username || "",
        name: selectedUser.name || "",
        xp: selectedUser.xp || 0,
        level: selectedUser.level || 1,
        emailVerified: selectedUser.emailVerified || false,
        infinityTokens: selectedUser.infinityTokens || 0,
        roles: selectedUser.roles || [],
      });
    } else {
      // Bulk mode: blank out all fields
      setFormData((prev) => ({
        ...prev,
        xp: 0,
        level: "",
        emailVerified: false,
        infinityTokens: 0,
        roles: [],
      }));
    }
  }, [open]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    const mergedFormData = { ...formData };

    for (const field of customProfileFields) {
      if (!field.key) continue;

      try {
        let val;
        switch (field.type) {
          case "string":
            val = field.value;
            break;
          case "number":
            val = Number(field.value);
            break;
          case "boolean":
            val = field.value.toLowerCase() === "true";
            break;
          case "timestamp":
            val = new Date(field.value);
            break;
          case "array":
          case "map":
            val = JSON.parse(field.value);
            break;
          default:
            val = field.value;
        }

        mergedFormData[field.key] = val;
      } catch (err) {
        alert(`❌ Invalid value for custom field: "${field.key}"`);
        return;
      }
    }

    onConfirm(mergedFormData);
    onClose();

    onConfirm(formData); // Let parent handle write logic
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{isBulk ? "Edit Users" : "Edit User"}</DialogTitle>
      <DialogContent>
        <Tabs
          value={tabIndex}
          onChange={(e, newIndex) => setTabIndex(newIndex)}
        >
          <Tab label="Profile" />
          <Tab label="Subcollections" />
        </Tabs>

        {tabIndex === 0 && (
          <>
            {!isBulk && (
              <>
                <TextField
                  fullWidth
                  label="Email"
                  margin="normal"
                  value={formData.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                />
                <TextField
                  fullWidth
                  label="Username"
                  margin="normal"
                  value={formData.username}
                  onChange={(e) => handleChange("username", e.target.value)}
                />
                <TextField
                  fullWidth
                  label="Name"
                  margin="normal"
                  value={formData.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                />
              </>
            )}

            <TextField
              fullWidth
              label="XP"
              margin="normal"
              type="number"
              value={formData.xp}
              onChange={(e) => handleChange("xp", Number(e.target.value))}
            />
            <TextField
              fullWidth
              label="Level"
              margin="normal"
              type="text"
              value={formData.level}
              onChange={(e) => handleChange("level", e.target.value)}
            />

            <TextField
              fullWidth
              label="Infinity Tokens"
              margin="normal"
              type="number"
              value={formData.infinityTokens}
              onChange={(e) =>
                handleChange("infinityTokens", Number(e.target.value))
              }
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.emailVerified}
                  onChange={(e) =>
                    handleChange("emailVerified", e.target.checked)
                  }
                />
              }
              label="Email Verified"
            />

            <FormControl fullWidth margin="normal">
              <InputLabel>Roles</InputLabel>
              <Select
                multiple
                value={formData.roles}
                onChange={(e) => handleChange("roles", e.target.value)}
                input={<OutlinedInput label="Roles" />}
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((role) => (
                      <Chip key={role} label={role} />
                    ))}
                  </Box>
                )}
              >
                {availableRoles.map((role) => (
                  <MenuItem key={role} value={role}>
                    {role}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box mt={4}>
              <Box mb={1} fontWeight="bold">
                Additional Fields
              </Box>

              {customProfileFields.map((field, index) => (
                <Box
                  key={index}
                  display="flex"
                  gap={1}
                  alignItems="center"
                  sx={{ mb: 2 }}
                >
                  <TextField
                    label="Key"
                    value={field.key}
                    onChange={(e) =>
                      setCustomProfileFields((prev) =>
                        prev.map((f, i) =>
                          i === index ? { ...f, key: e.target.value } : f
                        )
                      )
                    }
                  />
                  <FormControl sx={{ minWidth: 120 }}>
                    <InputLabel>Type</InputLabel>
                    <Select
                      value={field.type}
                      onChange={(e) =>
                        setCustomProfileFields((prev) =>
                          prev.map((f, i) =>
                            i === index ? { ...f, type: e.target.value } : f
                          )
                        )
                      }
                    >
                      <MenuItem value="string">String</MenuItem>
                      <MenuItem value="number">Number</MenuItem>
                      <MenuItem value="boolean">Boolean</MenuItem>
                      <MenuItem value="map">Map (JSON)</MenuItem>
                      <MenuItem value="array">Array (JSON)</MenuItem>
                      <MenuItem value="timestamp">Timestamp</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    label="Value"
                    value={field.value}
                    onChange={(e) =>
                      setCustomProfileFields((prev) =>
                        prev.map((f, i) =>
                          i === index ? { ...f, value: e.target.value } : f
                        )
                      )
                    }
                  />
                  <Button
                    color="error"
                    onClick={() =>
                      setCustomProfileFields((prev) =>
                        prev.filter((_, i) => i !== index)
                      )
                    }
                  >
                    ✕
                  </Button>
                </Box>
              ))}

              <Button
                variant="outlined"
                onClick={() =>
                  setCustomProfileFields((prev) => [
                    ...prev,
                    { key: "", type: "string", value: "" },
                  ])
                }
              >
                + Add Field
              </Button>
            </Box>
          </>
        )}

        {tabIndex === 1 && (
          <>
            <FormControl fullWidth margin="normal">
              <InputLabel>Select Subcollection</InputLabel>
              <Select
                value={selectedCollection}
                onChange={(e) => setSelectedCollection(e.target.value)}
              >
                {collections.map((col) => (
                  <MenuItem key={col} value={col}>
                    {col}
                  </MenuItem>
                ))}
                <MenuItem value="custom">Custom...</MenuItem>
              </Select>
            </FormControl>

            {selectedCollection === "custom" && (
              <TextField
                fullWidth
                label="Custom Collection Name"
                margin="normal"
                value={customCollection}
                onChange={(e) => setCustomCollection(e.target.value)}
              />
            )}

            <Box mt={2}>
              <FormLabel>Document Mode</FormLabel>
              <RadioGroup
                row
                value={docMode}
                onChange={(e) => setDocMode(e.target.value)}
              >
                <FormControlLabel
                  value="auto"
                  control={<Radio />}
                  label="Auto-ID"
                />
                <FormControlLabel
                  value="custom"
                  control={<Radio />}
                  label="Custom Doc ID"
                />
                <FormControlLabel
                  value="edit"
                  control={<Radio />}
                  label="Edit Existing Doc ID"
                />
              </RadioGroup>
            </Box>

            {docMode !== "auto" && (
              <TextField
                fullWidth
                label="Document ID"
                margin="normal"
                value={docId}
                onChange={(e) => setDocId(e.target.value)}
              />
            )}

            {subDocFields.map((field, index) => (
              <Box
                key={index}
                display="flex"
                gap={1}
                alignItems="center"
                sx={{ mb: 2 }}
              >
                <TextField
                  label="Key"
                  value={field.key}
                  onChange={(e) =>
                    setSubDocFields((prev) =>
                      prev.map((f, i) =>
                        i === index ? { ...f, key: e.target.value } : f
                      )
                    )
                  }
                />
                <FormControl sx={{ minWidth: 120 }}>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={field.type}
                    onChange={(e) =>
                      setSubDocFields((prev) =>
                        prev.map((f, i) =>
                          i === index ? { ...f, type: e.target.value } : f
                        )
                      )
                    }
                  >
                    <MenuItem value="string">String</MenuItem>
                    <MenuItem value="number">Number</MenuItem>
                    <MenuItem value="boolean">Boolean</MenuItem>
                    <MenuItem value="map">Map (JSON)</MenuItem>
                    <MenuItem value="array">Array (JSON)</MenuItem>
                    <MenuItem value="timestamp">Timestamp</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  label="Value"
                  value={field.value}
                  onChange={(e) =>
                    setSubDocFields((prev) =>
                      prev.map((f, i) =>
                        i === index ? { ...f, value: e.target.value } : f
                      )
                    )
                  }
                />
                <Button
                  color="error"
                  onClick={() =>
                    setSubDocFields((prev) =>
                      prev.filter((_, i) => i !== index)
                    )
                  }
                >
                  ✕
                </Button>
              </Box>
            ))}

            <Button
              variant="outlined"
              onClick={() =>
                setSubDocFields((prev) => [
                  ...prev,
                  { key: "", type: "string", value: "" },
                ])
              }
            >
              + Add Field
            </Button>

            <Button
              variant="contained"
              color="primary"
              sx={{ mt: 2 }}
              disabled={
                (!selectedCollection && !customCollection) ||
                (docMode !== "auto" && !docId.trim())
              }
              onClick={async () => {
                const collectionName =
                  selectedCollection === "custom"
                    ? customCollection.trim()
                    : selectedCollection;

                if (!collectionName) return alert("Collection name required");

                const docData = {};
                for (const field of subDocFields) {
                  if (!field.key) continue;

                  try {
                    let val;
                    switch (field.type) {
                      case "string":
                        val = field.value;
                        break;
                      case "number":
                        val = Number(field.value);
                        break;
                      case "boolean":
                        val = field.value.toLowerCase() === "true";
                        break;
                      case "timestamp":
                        val = new Date(field.value);
                        break;
                      case "array":
                      case "map":
                        val = JSON.parse(field.value);
                        break;
                      default:
                        val = field.value;
                    }
                    docData[field.key] = val;
                  } catch (err) {
                    alert(`❌ Invalid value for key "${field.key}"`);
                    return;
                  }
                }

                try {
                  const collectionName =
                    selectedCollection === "custom"
                      ? customCollection.trim()
                      : selectedCollection;

                  // Defensive
                  if (!collectionName) return alert("Collection name required");

                  for (const userId of selectedUserIds) {
                    const userRef = doc(db, "users", userId);
                    const subRef = collection(userRef, collectionName);

                    if (docMode === "auto") {
                      await addDoc(subRef, docData);
                    } else if (docMode === "custom") {
                      await setDoc(doc(subRef, docId.trim()), docData);
                    } else if (docMode === "edit") {
                      await updateDoc(doc(subRef, docId.trim()), docData);
                    }
                  }

                  alert(
                    `✅ Document ${
                      docMode === "auto" ? "added" : "written"
                    } to ${selectedUserIds.length} user(s)`
                  );

                  // Reset state
                  setSubDocFields([{ key: "", type: "string", value: "" }]);
                  setDocId("");
                } catch (err) {
                  console.error("❌ Firestore error:", err);
                  alert("Failed to write document.");
                }
              }}
            >
              Submit Document
            </Button>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit}>
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
}
